import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import type { ViewAutomation } from "../view-model";

interface AutomationsStore {
  // State
  automations: ViewAutomation[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;

  // Actions
  setAutomations: (automations: ViewAutomation[]) => void;
  updateAutomation: (
    automationId: string,
    updates: Partial<ViewAutomation>
  ) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearErrors: () => void;

  // Selectors
  getAutomations: () => ViewAutomation[];
  getAutomationsForDevice: (deviceId: string) => ViewAutomation[];
  getAutomationById: (automationId: string) => ViewAutomation | undefined;
}

export const useAutomationsStore = create<AutomationsStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      automations: [],
      isLoading: false,
      error: null,
      lastUpdated: 0,

      // Actions
      setAutomations: (automations: ViewAutomation[]) => {
        set({
          automations,
          lastUpdated: Date.now(),
          isLoading: false,
          error: null,
        });
      },

      updateAutomation: (
        automationId: string,
        updates: Partial<ViewAutomation>
      ) => {
        set((state) => ({
          automations: state.automations.map((automation) => {
            // Find the automation by checking if it has the matching ID
            const hasMatchingId = Object.values(automation).some(
              (automationData) => automationData?.id === automationId
            );

            if (hasMatchingId) {
              return { ...automation, ...updates };
            }
            return automation;
          }),
          lastUpdated: Date.now(),
        }));
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error, isLoading: false });
      },

      clearErrors: () => {
        set({ error: null });
      },

      // Selectors
      getAutomations: () => get().automations,

      getAutomationsForDevice: (deviceId: string) =>
        get().automations.filter((automation) =>
          Object.values(automation).some(
            (automationData) => automationData?.deviceId === deviceId
          )
        ),

      getAutomationById: (automationId: string) =>
        get().automations.find((automation) =>
          Object.values(automation).some(
            (automationData) => automationData?.id === automationId
          )
        ),
    })),
    {
      name: "automations-store", // Name that will appear in Redux DevTools
    }
  )
);
